import type { URLType } from '../../types'

/** Vite client: process.env is undefined; use import.meta.env. Node/Next.js: use process.env. */
function getBaseUrl(type: URLType): string | undefined {
  const m = typeof import.meta !== 'undefined' ? (import.meta as { env?: Record<string, string> }).env : undefined
  const p = process.env
  switch (type) {
    case 'help':
      return m?.NEXT_PUBLIC_HELP_URL ?? p.NEXT_PUBLIC_HELP_URL
    case 'partner':
      return m?.NEXT_PUBLIC_PARTNER_URL ?? p.NEXT_PUBLIC_PARTNER_URL
    case 'admin':
      return m?.NEXT_PUBLIC_ADMIN_URL ?? p.NEXT_PUBLIC_ADMIN_URL
    case 'marketing':
      return m?.NEXT_PUBLIC_MARKETING_URL ?? p.NEXT_PUBLIC_MARKETING_URL
    case 'api':
      return m?.NEXT_PUBLIC_API_URL ?? p.NEXT_PUBLIC_API_URL
    case 'school':
      return m?.NEXT_PUBLIC_SCHOOL_URL ?? p.NEXT_PUBLIC_SCHOOL_URL
    case 'account':
      return m?.NEXT_PUBLIC_ACCOUNT_URL ?? p.NEXT_PUBLIC_ACCOUNT_URL
    case 'web':
      return m?.NEXT_PUBLIC_WEB_URL ?? p.NEXT_PUBLIC_WEB_URL
    case 'content':
      return m?.NEXT_PUBLIC_CONTENT_URL ?? p.NEXT_PUBLIC_CONTENT_URL
    default:
      return undefined
  }
}

interface SearchParams {
  redirect_url?: string
  [key: string]: string | undefined | string[]
}

type BuildUrl = (type: URLType, endpoint: string, searchParams?: SearchParams) => URL

/**
 * Builds a URL object with the specified base URL type, endpoint path, and optional query parameters
 *
 * @param type - The type of URL to build (help, partner, admin, marketing, webhook, school, account, content, or web)
 * @param endpoint - The endpoint path to append to the base URL
 * @param searchParams - Optional query parameters to append to the URL
 * @returns A fully constructed URL object
 * @throws Error if the base URL for the specified type is not defined in environment variables
 *
 * @example
 * ```ts
 * const url = buildUrl('admin', '/users', { page: '1', limit: '10' })
 * // Returns: URL object for https://admin.example.com/users?page=1&limit=10
 * ```
 */
export const buildUrl: BuildUrl = (type, endpoint, searchParams) => {
  const baseUrl = getBaseUrl(type)

  if (!baseUrl) {
    throw new Error(`Base URL for type "${type}" is not defined in environment variables`)
  }

  const url = new URL(endpoint, baseUrl)

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const stringValue = Array.isArray(value) ? value.join(',') : String(value)
        url.searchParams.append(key, stringValue)
      }
    })
  }

  return url
}
